import React, { useEffect, useState, lazy, Suspense } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ArrowRight, Code, Shield, Zap, Users } from 'lucide-react';
import Navbar from '@/components/Navbar';
import OptimizedImage from '@/components/OptimizedImage';
import { API } from '@/App';
import axios from 'axios';

// Lazy load Footer
const Footer = lazy(() => import('@/components/Footer'));

const HomePage = () => {
  const [services, setServices] = useState([]);
  const [projects, setProjects] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Fetch settings immediately for hero section
    fetchSettings();

    // Only fetch data when user scrolls near content
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && services.length === 0) {
          fetchData();
        }
      },
      { rootMargin: '100px' }
    );

    const sentinel = document.getElementById('services-sentinel');
    if (sentinel) observer.observe(sentinel);

    return () => observer.disconnect();
  }, [services.length]);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings`);
      setSettings(response.data);
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  };

  const fetchData = async () => {
    try {
      const [servicesRes, projectsRes] = await Promise.all([
        axios.get(`${API}/services?active_only=true`),
        axios.get(`${API}/projects?active_only=true`),
      ]);
      setServices(servicesRes.data.slice(0, 4));
      setProjects(projectsRes.data.slice(0, 3));
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0f]">
      <Helmet>
        <title>{settings?.site_title || "Ener Yazılım - Dijital Dünyanın Güvenli Geleceği"}</title>
        <meta name="description" content={settings?.meta_description || "Yazılım, elektronik, bilişim güvenlik ve teknoloji alanında yenilikçi çözümler sunarak işletmenizi dijital dönüşüme hazırlıyoruz."} />
        <meta name="keywords" content={settings?.meta_keywords || "yazılım, siber güvenlik, AI geliştirme, web geliştirme, mobil uygulama, bulut hizmetleri, devops"} />
        <link rel="canonical" href="https://www.eneryazilim.com/" />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.eneryazilim.com/" />
        <meta property="og:title" content={settings?.site_title || "Ener Yazılım - Dijital Dünyanın Güvenli Geleceği"} />
        <meta property="og:description" content={settings?.meta_description || "Yazılım, elektronik, bilişim güvenlik ve teknoloji alanında yenilikçi çözümler sunarak işletmenizi dijital dönüşüme hazırlıyoruz."} />
        <meta property="og:image" content="https://www.eneryazilim.com/logo.png" />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content="https://www.eneryazilim.com/" />
        <meta name="twitter:title" content={settings?.site_title || "Ener Yazılım - Dijital Dünyanın Güvenli Geleceği"} />
        <meta name="twitter:description" content={settings?.meta_description || "Yazılım, elektronik, bilişim güvenlik ve teknoloji alanında yenilikçi çözümler sunarak işletmenizi dijital dönüşüme hazırlıyoruz."} />
        <meta name="twitter:image" content="https://www.eneryazilim.com/logo.png" />
        
        {/* Structured Data - Organization */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Organization",
            "name": "Ener Yazılım Elektronik Bilişim Güvenlik Teknolojileri",
            "url": "https://www.eneryazilim.com",
            "logo": "https://www.eneryazilim.com/logo.png",
            "description": "Yazılım, elektronik, bilişim güvenlik ve teknoloji çözümleri",
            "address": {
              "@type": "PostalAddress",
              "addressCountry": "TR"
            },
            "sameAs": []
          })}
        </script>
      </Helmet>
      <Navbar />

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0 bg-gradient-to-br from-[#FF6B35]/10 via-transparent to-[#F7931E]/10"></div>
        {/* Simplified blur effects for better performance */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-20 left-10 w-72 h-72 bg-[#FF6B35]/20 rounded-full blur-[100px]"></div>
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-[#F7931E]/20 rounded-full blur-[100px]"></div>
        </div>

        <div className="container mx-auto px-4 relative z-10 pt-20">
          <div className="max-w-4xl mx-auto text-center">
            <div className="animate-fadeIn">
              <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold mb-6 leading-tight" data-testid="hero-title">
                {settings?.hero_title || "Dijital Dünyanın"}
                <span className="block gradient-text mt-2">{settings?.hero_title_highlight || "Güvenli Geleceği"}</span>
              </h1>
              <p className="text-lg sm:text-xl text-white/70 mb-8 max-w-2xl mx-auto" data-testid="hero-description">
                {settings?.hero_description || "Yazılım, elektronik, bilişim güvenlik ve teknoloji alanında yenilikçi çözümler sunarak işletmenizi dijital dönüşüme hazırlıyoruz."}
              </p>
              <div className="flex flex-wrap gap-4 justify-center">
                <Link
                  to={settings?.hero_cta_primary_link || "/hizmetler"}
                  data-testid="cta-services-button"
                  className="px-8 py-4 bg-gradient-to-r from-[#FF6B35] to-[#F7931E] rounded-full font-semibold hover:shadow-lg hover:shadow-[#FF6B35]/50 transition-all btn-hover"
                >
                  {settings?.hero_cta_primary_text || "Hizmetlerimiz"}
                </Link>
                <Link
                  to={settings?.hero_cta_secondary_link || "/iletisim"}
                  data-testid="cta-contact-button"
                  className="px-8 py-4 glass rounded-full font-semibold hover:bg-white/10 transition-all"
                >
                  {settings?.hero_cta_secondary_text || "İletişime Geç"}
                </Link>
              </div>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-20">
              {(settings?.stats || [
                { icon: 'Code', value: '500+', label: 'Proje' },
                { icon: 'Users', value: '300+', label: 'Mutlu Müşteri' },
                { icon: 'Shield', value: '%99.9', label: 'Güvenlik' },
                { icon: 'Zap', value: '7/24', label: 'Destek' },
              ]).map((stat, index) => {
                // Map icon names to components
                const IconComponent = {
                  Code, Users, Shield, Zap
                }[stat.icon] || Code;

                return (
                  <div
                    key={index}
                    data-testid={`stat-${index}`}
                    className="glass p-6 rounded-2xl card-hover"
                    style={{ animationDelay: `${index * 0.1}s` }}
                  >
                    <IconComponent className="w-8 h-8 text-[#FF6B35] mx-auto mb-3" />
                    <div className="text-3xl font-bold gradient-text mb-1">{stat.value}</div>
                    <div className="text-sm text-white/60">{stat.label}</div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Intersection observer sentinel */}
      <div id="services-sentinel" className="h-1"></div>

      {/* Services Preview */}
      {services.length > 0 && (
        <section className="py-20 bg-gradient-to-b from-transparent to-[#0a0a0f]" data-testid="services-section">
          <div className="container mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-bold mb-4">Hizmetlerimiz</h2>
              <p className="text-white/60 max-w-2xl mx-auto">Size özel çözümlerle dijital dönüşüm yolculuğunuzda yanınızdayız</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {services.map((service) => (
                <div key={service.id} className="glass p-6 rounded-2xl card-hover" data-testid={`service-card-${service.id}`}>
                  <div className="text-4xl mb-4">{service.icon}</div>
                  <h3 className="text-xl font-semibold mb-3">{service.title}</h3>
                  <p className="text-sm text-white/60">{service.description}</p>
                </div>
              ))}
            </div>
            <div className="text-center mt-8">
              <Link
                to="/hizmetler"
                data-testid="view-all-services-link"
                className="inline-flex items-center space-x-2 text-[#FF6B35] hover:text-[#F7931E] transition-colors"
              >
                <span>Tüm Hizmetleri Gör</span>
                <ArrowRight size={18} />
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Projects Preview */}
      {projects.length > 0 && (
        <section className="py-20" data-testid="projects-section">
          <div className="container mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-bold mb-4">Projelerimiz</h2>
              <p className="text-white/60 max-w-2xl mx-auto">Gerçekleştirdiğimiz başarılı projelerle işletmelere değer katıyoruz</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {projects.map((project) => (
                <div key={project.id} className="glass rounded-2xl overflow-hidden card-hover" data-testid={`project-card-${project.id}`}>
                  {project.image_url ? (
                    <OptimizedImage
                      src={project.image_url}
                      alt={project.title}
                      className="h-48"
                      loading="lazy"
                      objectFit="cover"
                    />
                  ) : (
                    <div className="h-48 bg-gradient-to-br from-[#FF6B35]/20 to-[#F7931E]/20 flex items-center justify-center">
                      <div className="text-6xl opacity-50">💻</div>
                    </div>
                  )}
                  <div className="p-6">
                    <div className="text-sm text-[#FF6B35] mb-2">{project.category}</div>
                    <h3 className="text-xl font-semibold mb-2">{project.title}</h3>
                    <p className="text-sm text-white/60 mb-4">{project.description}</p>
                    <div className="flex flex-wrap gap-2">
                      {project.technologies?.slice(0, 3).map((tech, i) => (
                        <span key={i} className="text-xs px-3 py-1 bg-white/5 rounded-full">{tech}</span>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className="text-center mt-8">
              <Link
                to="/projeler"
                data-testid="view-all-projects-link"
                className="inline-flex items-center space-x-2 text-[#FF6B35] hover:text-[#F7931E] transition-colors"
              >
                <span>Tüm Projeleri Gör</span>
                <ArrowRight size={18} />
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="py-20 relative overflow-hidden" data-testid="cta-section">
        <div className="absolute inset-0 bg-gradient-to-r from-[#FF6B35]/10 to-[#F7931E]/10"></div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">Projenizi Hayata Geçirelim</h2>
            <p className="text-lg text-white/70 mb-8">
              Dijital dönüşüm yolculuğunuzda size rehberlik edelim. Hemen iletişime geçin!
            </p>
            <Link
              to="/iletisim"
              data-testid="cta-bottom-button"
              className="inline-flex items-center space-x-2 px-8 py-4 bg-gradient-to-r from-[#FF6B35] to-[#F7931E] rounded-full font-semibold hover:shadow-lg hover:shadow-[#FF6B35]/50 transition-all btn-hover"
            >
              <span>İletişime Geç</span>
              <ArrowRight size={20} />
            </Link>
          </div>
        </div>
      </section>

      <Suspense fallback={<div className="h-64"></div>}>
        <Footer />
      </Suspense>
    </div>
  );
};

export default HomePage;